import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  Image,
  VStack,
  Badge,
  HStack,
  Icon,
  useColorModeValue,
  Divider,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FaArrowRight, FaDna } from "react-icons/fa";

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

function Hero() {
  const sectionBg = useColorModeValue(
    "rgba(255, 255, 255, 0.4)",
    "rgba(11, 19, 41, 0.4)"
  );
  const cardBorder = useColorModeValue("slate.200", "whiteAlpha.100");
  const headingColor = useColorModeValue("slate.800", "white");
  const textColor = useColorModeValue("slate.600", "slate.300");

  return (
    <MotionBox
      id="home"
      as="section"
      position="relative"
      overflow="hidden"
      pt={{ base: "120px", md: "150px" }}
      pb={{ base: 12, md: 16 }}
      bg={sectionBg}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      {/* Background Ambient Glows */}
      <Box
        position="absolute"
        top="-10%"
        left="-5%"
        w="400px"
        h="400px"
        borderRadius="full"
        bg="radial-gradient(circle, rgba(13, 148, 136, 0.18) 0%, rgba(0,0,0,0) 70%)"
        pointerEvents="none"
      />
      <Box
        position="absolute"
        bottom="0%"
        right="-5%"
        w="450px"
        h="450px"
        borderRadius="full"
        bg="radial-gradient(circle, rgba(6, 182, 212, 0.15) 0%, rgba(0,0,0,0) 70%)"
        pointerEvents="none"
      />

      <Flex
        direction={{ base: "column", md: "row" }}
        align="center"
        justify="space-between"
        maxW="1200px"
        mx="auto"
        px={6}
        position="relative"
        zIndex="1"
      >
        {/* Text Section */}
        <MotionFlex
          direction="column"
          align="flex-start"
          maxW="620px"
          initial={{ x: -60, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <Heading
            size="2xl"
            color={headingColor}
            mb={5}
            lineHeight="1.15"
            letterSpacing="-0.02em"
            fontWeight="900"
          >
            Discover Genetic Insights into{" "}
            <Text
              as="span"
              bgGradient="linear(to-r, teal.500, cyan.400)"
              bgClip="text"
            >
              XP in Tunisia
            </Text>
          </Heading>

          <Text
            fontSize="lg"
            color={textColor}
            mb={8}
            lineHeight="1.7"
            fontWeight="400"
          >
            Explore the Tunisian XP Database, a comprehensive resource dedicated to understanding xeroderma pigmentosum. Delve into the research, data, and genetic insights that can help in combating this rare disease.
          </Text>

          <Flex gap={4} align="center">
            <MotionBox
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
            >
              <Button
                size="lg"
                colorScheme="teal"
                bgGradient="linear(to-r, teal.500, cyan.500)"
                color="white"
                borderRadius="full"
                px="32px"
                py="24px"
                fontSize="md"
                fontWeight="700"
                shadow="0 10px 25px rgba(13, 148, 136, 0.4)"
                rightIcon={<FaArrowRight />}
                _hover={{
                  bgGradient: "linear(to-r, teal.600, cyan.600)",
                  shadow: "0 14px 30px rgba(13, 148, 136, 0.5)",
                }}
                as={Link}
                to="/txd"
              >
                Explore the Database
              </Button>
            </MotionBox>
          </Flex>
        </MotionFlex>

        {/* Image Section */}
        <MotionBox
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          mt={{ base: 10, md: 0 }}
          position="relative"
        >
          <Box
            position="relative"
            p="12px"
            bg={useColorModeValue("rgba(255, 255, 255, 0.7)", "rgba(15, 23, 42, 0.7)")}
            backdropFilter="blur(20px)"
            borderRadius="32px"
            border="1px solid"
            borderColor={cardBorder}
            shadow={useColorModeValue("0 20px 40px rgba(0,0,0,0.08)", "0 20px 50px rgba(0,0,0,0.5)")}
          >
            <Image
              src="/imgs/lab.jpg"
              alt="Genetic research illustration"
              maxW={{ base: "100%", md: "460px" }}
              maxH="420px"
              objectFit="cover"
              borderRadius="24px"
            />
          </Box>
        </MotionBox>
      </Flex>

      <Divider my={10} maxW="1200px" mx="auto" borderColor={cardBorder} />
    </MotionBox>
  );
}

export default Hero;

